import { IEnquiryEntry } from "@/utilities/types/EnquiryEntry.ts";
import { ActionIcon, Button } from "rizzui";
import { useEffect, useState } from "react";
import useApi from "@/hooks/useApi.tsx";
import EnquiryEntriesRepository from "@/utilities/repositories/EnquiryEntries.ts";
import { useModal } from "@/app/shared/modal-views/use-modal.ts";
import QuillEditor from "@/components/ui/quille-editor/quill-editor.tsx";
import { PiXBold } from "react-icons/pi";
import { LuNotebookPen } from "react-icons/lu";

interface NotesProps {
  data: IEnquiryEntry;
  isModalView?: boolean;
  onRefresh?: () => void;
  isAdmin?: boolean;
}

export const Notes = ({ data, onRefresh, isAdmin }: NotesProps) => {
  const [userNote, setUserNote] = useState<string | null>(null);
  const [adminNote, setAdminNote] = useState<string | null>(null);

  const { closeModal } = useModal();

  const isEnquiryEntry = (obj?: any): obj is IEnquiryEntry => {
    return "note" in obj;
  };
  useEffect(() => {
    if (!data) return;
    if (!data?.id) return;

    if (isEnquiryEntry(data)) {
      data.note && setUserNote(data.note);
      data.adminNote && setAdminNote(data.adminNote);
    }
  }, [data?.id, isEnquiryEntry(data)]);

  const { request: updateEnquiryEntry, loading } = useApi({
    request: EnquiryEntriesRepository.update,
    enableToast: true,
  });

  return (
    <div className={"col-span-full flex flex-col gap-10 p-6 relative"}>
      <div className={"flex justify-between items-center"}>
        <div className="flex items-center gap-3">
          <LuNotebookPen className="h-6 w-6" />
          <span className="text-2xl font-medium">Note</span>
        </div>
        <ActionIcon
          size="sm"
          variant="text"
          onClick={() => closeModal()}
          className="p-0 text-gray-500 hover:!text-gray-900"
        >
          <PiXBold className="h-5 w-5" />
        </ActionIcon>
      </div>
      <QuillEditor
        value={userNote}
        onChange={(value) => setUserNote(value)}
        label="User Notes"
        className="[&_.ql-editor]:min-h-[130px] [&_.ql-editor]:max-h-[200px] text-lg"
        labelClassName="font-medium text-gray-700 dark:text-gray-600 mb-1.5"
        description="Key in your notes here."
        descriptionClassName="text-gray-500 text-sm mb-1.5"
        error={data.note === userNote ? undefined : ""}
        modules={{
          toolbar: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ color: [] }, { background: [] }],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link", "clean"],
          ],
        }}
      />
      <QuillEditor
        value={adminNote}
        onChange={(value) => setAdminNote(value)}
        label="ProjectSG Notes"
        className="[&_.ql-editor]:min-h-[130px] [&_.ql-editor]:max-h-[200px] text-lg"
        labelClassName="font-medium text-gray-700 dark:text-gray-600 mb-1.5"
        description="Editor access is restricted to ProjectSG"
        descriptionClassName="text-gray-500 text-sm mb-1.5"
        readOnly={!isAdmin}
        error={data.note === adminNote ? undefined : ""}
        modules={{
          toolbar: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ color: [] }, { background: [] }],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link", "clean"],
          ],
        }}
      />
      <div className="flex justify-end">
        <Button
          disabled={data.note === userNote && data.adminNote === adminNote}
          isLoading={loading}
          onClick={async () => {
            if (!data?.id) return;
            try {
              await updateEnquiryEntry(data.id, {
                note: userNote,
                adminNote: adminNote,
              });
              closeModal();
            } catch (error) {}

            onRefresh && onRefresh();
          }}
        >
          Save
        </Button>
      </div>
    </div>
  );
};
